/**
 * A sandbox call's op run on this tab's executor, answered in C2's wire shape (backend
 * apps/sandbox/executor.py RESULTS, core/hostCalls.ts SandboxOpResults): the executor speaks C1's
 * camel case, the socket snake case. `stage` syncs the file through the workspace's host session
 * (C6), on the session's seqs. `load` is not run yet (it waits on C1's Executor.mount). Every
 * string handed in is well formed (`wellFormed`).
 */
import { gzip, sha256Hex } from "../core/bytes";
import { SandboxError } from "../core/errors";
import type {
  SandboxClientCall,
  SandboxOpArgs,
  SandboxOpResults,
  WireExecResult,
} from "../core/hostCalls";
import type {
  ExecResult,
  Executor,
  PatchChange,
  ReadOptions,
  ReadResult,
  ReplOptions,
  ReplResult,
  WriteResult,
} from "../core/types";
import type { HostSession } from "../sync/session";
import { recordCell } from "./cellRecords";

const WORKSPACE = "/workspace/";
/** What Postgres jsonb refuses (NUL) and what UTF-8 cannot encode (a surrogate left unpaired, as a
 * slice of the output can leave one). */
const ILL_FORMED = /\0|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;

export type OpResult = SandboxOpResults[keyof SandboxOpResults];

/** `text` (cut to `max` characters first) as the server can store it: NUL and unpaired surrogates
 * become U+FFFD. */
export function wellFormed(text: string, max = Infinity): string {
  return (text.length > max ? text.slice(0, max) : text).replace(ILL_FORMED, "\uFFFD");
}

/** Every string in a wire result made `wellFormed`. */
export function wellFormedResult<T>(value: T): T {
  if (typeof value === "string") return wellFormed(value) as T;
  if (Array.isArray(value)) return value.map(wellFormedResult) as T;
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, wellFormedResult(item)])
    ) as T;
  }
  return value;
}

export async function runSandboxOp(
  executor: Executor,
  session: HostSession,
  call: SandboxClientCall
): Promise<OpResult> {
  return wellFormedResult(await runOp(executor, session, call));
}

async function runOp(
  executor: Executor,
  session: HostSession,
  call: SandboxClientCall
): Promise<OpResult> {
  switch (call.op) {
    case "bash":
      return wireExec(await executor.bash(call.args.command));
    case "repl": {
      const result = await executor.repl(
        call.args.language,
        call.args.code,
        replOptions(call.args)
      );
      recordCell(call.id, result);
      return wireRepl(result);
    }
    case "read":
      return wireRead(await executor.read(call.args.path, readOptions(call.args)));
    case "write":
      return wireWrite(await executor.write(call.args.path, call.args.content));
    case "edit":
      return wireWrite(await executor.edit(call.args.path, call.args.old, call.args.new));
    case "apply_patch":
      return wirePatch(await executor.applyPatch(call.args.patch));
    case "stage":
      return stage(executor, session, call.args.path);
    case "load":
      // Waits on C1's Executor.mount (deferred, ST2); CONTRACTS.md says what a host-page fetch needs.
      throw new SandboxError("NOT_STARTED", "NOT_STARTED: this sandbox cannot load exports yet");
    default:
      throw new SandboxError("EINVAL", "EINVAL: unknown sandbox op");
  }
}

/** A parked cell's replay (C14, PK3): the server sends `cell_id` and `journal` only then. */
function replOptions(args: SandboxOpArgs["repl"]): ReplOptions | undefined {
  if (args.cell_id == null && args.journal == null) return undefined;
  return {
    ...(args.cell_id != null && { cellId: args.cell_id }),
    ...(args.journal != null && { journal: args.journal }),
  };
}

function readOptions(args: { offset?: number; limit?: number; pages?: string }): ReadOptions {
  const options: ReadOptions = {};
  if (args.offset != null) options.offset = args.offset;
  if (args.limit != null) options.limit = args.limit;
  if (args.pages != null) options.pages = args.pages;
  return options;
}

export function wireExec(result: ExecResult): WireExecResult {
  return {
    stdout: result.stdout,
    stderr: result.stderr,
    exit_code: result.exitCode,
    truncated: result.truncated,
    duration_ms: Math.max(0, Math.round(result.durationMs)),
    ...(result.timedOut != null && { timed_out: result.timedOut }),
  };
}

export function wireRepl(result: ReplResult): SandboxOpResults["repl"] {
  return {
    ...wireExec(result),
    ...(result.value !== undefined && { value: result.value }),
    ...(result.resetState != null && { reset_state: result.resetState }),
    ...(result.parked && {
      parked: {
        choices: result.parked.choices,
        journal_length: result.parked.journalLength,
        journal: result.parked.journal,
      },
    }),
  };
}

export function wireRead(result: ReadResult): SandboxOpResults["read"] {
  return {
    path: result.path,
    kind: result.kind,
    text: result.text,
    truncated: result.truncated,
    ...(result.totalLines != null && { total_lines: result.totalLines }),
    ...(result.totalPages != null && { total_pages: result.totalPages }),
  };
}

export function wireWrite(result: WriteResult): SandboxOpResults["write"] {
  return { path: result.path, bytes: result.bytes };
}

/** C2 wraps the changes: `{changes: [...]}`, never a bare list. */
export function wirePatch(changes: PatchChange[]): SandboxOpResults["apply_patch"] {
  return {
    changes: changes.map((change) => ({
      path: change.path,
      op: change.op,
      ...(change.movedTo != null && { moved_to: change.movedTo }),
    })),
  };
}

/** The file's bytes now, synced as one put on the session's next seq, so the server can read them. */
async function stage(
  executor: Executor,
  session: HostSession,
  path: string
): Promise<SandboxOpResults["stage"]> {
  if (!path.startsWith(WORKSPACE)) {
    throw new SandboxError(
      "EINVAL",
      `EINVAL: only files under ${WORKSPACE} are kept, stage '${path}'`
    );
  }
  const bytes = await executor.exportFile(path);
  const sha256 = await sha256Hex(bytes);
  const body = await gzip(bytes);
  const relative = path.slice(WORKSPACE.length);
  await session.send([{ op: "put", path: relative, sha256, body }]);
  return { path, sha256, size: bytes.length };
}
